import type { ModuleRenderer } from './renderer.ts';
import { pocBoardClientRenderer } from './poc-board/client.tsx';
import { fixtureDiceBoardClientRenderer } from './fixture-dice-board/client.tsx';
import { pocChoiceClientRenderer } from './poc-choice/client.tsx';
import { fixtureTeam2v2ClientRenderer } from './fixture-team-2v2/client.tsx';

/**
 * Client-side module renderer registry. Import path: `@momentum/modules/client`. Keyed by
 * `manifest.capabilities.renderer`, resolved by `InstanceHost` and handed `ModuleRendererProps`.
 *
 * `poc-board` and `fixture-dice-board` both declare `capabilities.renderer: 'board'`, so they share one entry
 * (`BoardRenderer` in `./board/BoardRenderer.tsx`) — the two modules' `project()` output is what differs, not
 * the rendering code. `poc-choice` and `fixture-team-2v2` each have their own bespoke renderer.
 */
export const clientRenderers: Record<string, ModuleRenderer> = {
  [pocBoardClientRenderer.renderer]: pocBoardClientRenderer,
  [pocChoiceClientRenderer.renderer]: pocChoiceClientRenderer,
  [fixtureTeam2v2ClientRenderer.renderer]: fixtureTeam2v2ClientRenderer,
};

// fixtureDiceBoardClientRenderer registers under the same 'board' key as pocBoardClientRenderer (both modules
// declare capabilities.renderer: 'board'); keeping the import (rather than dropping it) documents that both
// modules were intentionally wired to the shared board renderer, and this assertion catches drift if either
// module's manifest ever points 'board' at a different component.
if (fixtureDiceBoardClientRenderer.Component !== clientRenderers.board.Component) {
  throw new Error('fixture-dice-board and poc-board must share the same board renderer component');
}

/**
 * Registers the optional 3D board renderer. `@momentum/modules` never imports `three` or client code directly;
 * the client app calls this once at startup (see `packages/client/src/renderers/index.ts`'s `setupRenderers()`)
 * with a lazily-imported component. Re-exported here because client bundles may only import `@momentum/shared`,
 * `@momentum/modules/client`, `@momentum/modules/manifests` and `@momentum/modules/renderer` (see
 * docs/ARCHITECTURE.md §1) — there is no separate `@momentum/modules/board/registry3d` entry point.
 */
export { registerBoard3D, type Board3DComponent, type Board3DProps } from './board/registry3d.ts';

/**
 * Pure geometry helpers (`ringLayout`, `fitPointsToBox`, `pathPositionAt` — see layout.ts) re-exported for the
 * same reason: `board3d.tsx` needs the ring-layout fallback for boards without positions, and must reach it
 * through this allowed import path rather than a cross-package relative import.
 */
export { ringLayout, fitPointsToBox, pathPositionAt, type Point2D } from './board/layout.ts';


/** Shared predicate for the immersive ceramic worlds, keyed on `graph.meta.assetKey`. */
export { isMomentumWorld } from './board/momentumWorld.ts';
